import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useAuth } from './contexts/AuthContext';
import { useSession } from './hooks/useSession';
import { useLocalization } from './contexts/LocalizationContext';

import Identification from './pages/Login/Identification';
import NotificationManager from './components/NotificationManager';
import ConfirmToast from './components/modals/ConfirmToast';
import Navigation from './components/Navigation';
import SystemMessageBanner from './components/SystemMessageBanner';
import LocalLogin from './pages/Login/LocalLogin';
import RootModal from './components/modals/RootModal';
import Loading from './components/Loading';
import Footer from './components/Footer';
import './App.scss';

const LandingPage = lazy(() =>
  import(
    /* webpackChunkName: "Landing" */ './pages/organization/student/LandingPage'
  )
);
const NewApplicationPage = lazy(() =>
  import(
    /* webpackChunkName: "NewApplication" */ './pages/organization/student/NewApplicationPage'
  )
);
const PaymentPage = lazy(() =>
  import(
    /* webpackChunkName: "Payment" */ './pages/organization/student/PaymentPage'
  )
);
const ApplicationPage = lazy(() =>
  import(
    /* webpackChunkName: "Application" */ './pages/organization/Application'
  )
);
const AdminLocalizations = lazy(() =>
  import(/* webpackChunkName: "Localizations" */ './pages/admin/Localizations')
);
const AdminOrganizations = lazy(() =>
  import(/* webpackChunkName: "Organizations" */ './pages/admin/Organizations')
);
const TenantView = lazy(() =>
  import(/* webpackChunkName: "Tenants" */ './pages/admin/Tenants')
);
const AdminUser = lazy(() =>
  import(/* webpackChunkName: "User" */ './pages/admin/User')
);
const AdminUsers = lazy(() =>
  import(/* webpackChunkName: "Users" */ './pages/admin/Users')
);
const AdminSystemMessages = lazy(() =>
  import(
    /* webpackChunkName: "SystemMessages" */ './pages/admin/SystemMessages'
  )
);
const SearchPage = lazy(() =>
  import(
    /* webpackChunkName: "Search" */ './pages/organization/processor/Search'
  )
);
const StudentPage = lazy(() =>
  import(
    /* webpackChunkName: "Student" */ './pages/organization/processor/Student'
  )
);
const HandleForms = lazy(() =>
  import(
    /* webpackChunkName: "ApplicationForm" */ './pages/organization/student/HandleForms'
  )
);
const StudentApplicationSearchPage = lazy(() =>
  import(
    /* webpackChunkName: "StudentApplicationSearch" */ './pages/organization/student/StudentApplicationSearch'
  )
);

const HandlingHistory = lazy(() =>
  import(
    /* webpackChunkName: "HandlingHistory" */ './pages/organization/processor/HandlingHistory'
  )
);
const HandlingPage = lazy(() =>
  import(
    /* webpackChunkName: "Handling" */ './pages/organization/processor/Handling'
  )
);
const HandleTask = lazy(() =>
  import(
    /* webpackChunkName: "HandleTask" */ './pages/organization/processor/HandleTask'
  )
);

const OpenApplicationPage = lazy(() =>
  import(
    /* webpackChunkName: "OpenApplications" */ './pages/organization/student/OpenApplicationPage'
  )
);
const ProtectedRoute = lazy(() =>
  import(/* webpackChunkName: "ProtectedRoute" */ './components/ProtectedRoute')
);

const ErrorPage = lazy(() =>
  import(/* webpackChunkName: "ErrorPage" */ './ErrorPage')
);

import { getBaseRoute } from './utility';

function App() {
  const { localize } = useLocalization();
  const { isLoggedIn, role, user, authLevel } = useAuth();
  const [baseRoute, setBaseRoute] = useState();
  const location = useLocation();

  useSession();

  useEffect(() => {
    if (!user || !role) return;

    setBaseRoute(getBaseRoute(user, role));
  }, [user, role]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [location.pathname]);

  return (
    <>
      <NotificationManager />
      <RootModal />
      <ConfirmToast />
      <div className='App'>
        {isLoggedIn && user && (
          <a
            href={`${location.pathname}${location.search}#content`}
            className='skip-link'>
            {localize('common.skipToMainContent', { editable: false })}
          </a>
        )}
        {isLoggedIn && user && <Navigation />}
        {isLoggedIn && user && <SystemMessageBanner />}
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route exact path='/login' element={<Identification />} />
            <Route exact path='/login/local' element={<LocalLogin />} />
            <Route
              path='/organization/:orgName/student'
              element={
                <ProtectedRoute
                  redirectPath={baseRoute}
                  isAllowed={role == 'student'}
                />
              }>
              <Route index element={<LandingPage />} />
              <Route
                path='open-application'
                element={<OpenApplicationPage />}
              />
              <Route
                path='archive'
                element={<StudentApplicationSearchPage />}
              />
              <Route path='new-application' element={<NewApplicationPage />} />
              <Route path='payments' element={<PaymentPage />} />
              <Route path='application/:id' element={<ApplicationPage />} />
              <Route path='form/:id' element={<HandleForms />} />
            </Route>

            <Route
              path='/admin'
              element={
                <ProtectedRoute
                  isAllowed={
                    role == 'root' || role == 'admin' || role === 'maintainer'
                  }
                />
              }>
              <Route
                path='organizations'
                element={
                  <ProtectedRoute
                    redirectPath={baseRoute}
                    isAllowed={role === 'root'}>
                    <AdminOrganizations />
                  </ProtectedRoute>
                }
              />
              <Route path='localizations' element={<AdminLocalizations />} />
              <Route
                path='system-messages'
                element={
                  <ProtectedRoute
                    redirectPath={baseRoute}
                    isAllowed={role === 'root'}>
                    <AdminSystemMessages />
                  </ProtectedRoute>
                }
              />
              <Route
                path='users'
                element={
                  <ProtectedRoute
                    redirectPath={baseRoute}
                    isAllowed={role === 'root'}>
                    <AdminUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path='users/:id'
                element={
                  <ProtectedRoute
                    redirectPath={baseRoute}
                    isAllowed={role === 'root'}>
                    <AdminUser />
                  </ProtectedRoute>
                }
              />
              <Route path=':tenant' element={<TenantView />} />
            </Route>
            <Route
              path='/students/:studentId'
              element={
                <ProtectedRoute
                  redirectPath={baseRoute}
                  isAllowed={
                    role == 'root' ||
                    role == 'admin' ||
                    role == 'handler' ||
                    role == 'maintainer'
                  }>
                  <StudentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/handler'
              element={
                <ProtectedRoute
                  isAllowed={
                    role == 'root' || role == 'admin' || role === 'handler'
                  }
                />
              }>
              <Route
                path='handlingHistory'
                element={
                  <ProtectedRoute
                    redirectPath={baseRoute}
                    isAllowed={
                      role == 'root' || role == 'admin' || role === 'handler'
                    }>
                    <HandlingHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path='handling'
                element={
                  <ProtectedRoute
                    redirectPath={baseRoute}
                    isAllowed={
                      role == 'root' ||
                      role == 'admin' ||
                      role === 'maintainer' ||
                      role === 'handler'
                    }>
                    <HandlingPage />
                  </ProtectedRoute>
                }
              />
              <Route path='search' element={<SearchPage />} />
              <Route path='handling/task/:id' element={<HandleTask />} />
            </Route>
            <Route path='/error' element={<ErrorPage />} />
            <Route path='*' element={<Navigate to={'/login'} replace />} />
          </Routes>
        </Suspense>
      </div>
      <Footer />
    </>
  );
}

export default App;
